import React from "react";
import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import Images from "../../request/components/Images";
import moment from "moment";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { Link as RouterLink } from "react-router-dom";
import { bakeStreetSquareLogo } from "../../common/constants";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import currency from "currency.js";
import ScheduleSendOutlinedIcon from "@mui/icons-material/ScheduleSendOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import UserAvatar from "../../common/UserAvatar";

export default function BakerQuoteSummary(props) {
  const { request } = props;

  let status = "";
  let total = "";
  let itemCount = 0;

  const quoteItems = request.quote?.items;
  const hasQuoteItems = Array.isArray(quoteItems) && quoteItems.length > 0;

  let pics = request.pics || [request.pic1, request.pic2, request.pic3].filter((i) => i);
  let displayDate = request.date;

  if (hasQuoteItems) {
    itemCount = quoteItems.length;
    total = quoteItems.reduce((acc, o) => +acc + (+o.total || +o.price), 0);
    pics = quoteItems.flatMap((item) => item.images || []).filter(Boolean);
    if (request.quote.fulfillment?.date) {
      displayDate = request.quote.fulfillment.date;
    }
  }

  if (request.quote?.status === "pending") {
    if (request.quote?.quotedBy) {
      status = "draft";
    } else {
      status = "pending";
    }
  } else {
    status = "sent";
    if (!hasQuoteItems && request.quote) {
      total = request.quote.quote;
    }
  }

  const actionRow =
    status === "pending" || status === "draft"
      ? {
          buttonLabel: status === "draft" ? "View Draft" : "Send Quote",
          variant: status === "draft" ? "outlined" : "contained",
          color: "primary",
        }
      : status === "sent"
        ? {
            buttonLabel: "View Quote",
            variant: "contained",
            color: "secondary",
            showAutoQuote: true,
          }
        : status === "expired"
          ? {
              buttonLabel: "View Quote",
              variant: "outlined",
              color: "secondary",
              unavailableLabel: "unavailable",
            }
          : null;

  const actionBoxSx = {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    alignItems: "center",
  };

  const reqTsMoment = request.quote?.reqTs?._seconds
    ? moment(request.quote.reqTs._seconds * 1000)
    : null;
  const reqTsOverdue = reqTsMoment && moment().diff(reqTsMoment, "hours") > 24;

  return (
    <Paper key={request.id} elevation={0}>
      <Box fontSize={16} px={2} pt={2} pb={1} mb={4}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={4} sm={3}>
            <Images pics={pics} />
          </Grid>
          <Grid item xs={8} sm={9} sx={{ display: "flex" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                width: "100%",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box>
                  <Typography variant="h6" display="inline">
                    {request.firstName
                      ? `${request.firstName} ${request.lastName || ""}`
                      : request.name}
                  </Typography>
                  {request.quote && request.quote.status === "paid" && (
                    <Chip
                      label="order"
                      size="small"
                      style={{ color: "#fff", backgroundColor: "#1abc9c" }}
                    />
                  )}
                </Box>
                <Box my={1}>
                  <Typography
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: ".80rem",
                      textTransform: "uppercase",
                      color: "#888",
                    }}
                  >
                    <EventOutlinedIcon style={{ fontSize: "1.2rem", marginRight: "7px" }} />
                    {displayDate ? moment(displayDate).format("ddd, MMM DD, YYYY") : ""}
                  </Typography>
                </Box>
                {total && (
                  <Box my={1}>
                    <Typography
                      variant="overline"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: ".8rem",
                        textTransform: "uppercase",
                        gap: "5px",
                        color: "#888",
                      }}
                    >
                      <ReceiptLongOutlinedIcon style={{ fontSize: "1.2rem" }} />
                      {currency(total).format()}
                      {itemCount > 0 && (
                        <>
                          <span style={{ color: "#CCC" }}>{` ● `}</span>
                          {itemCount} item(s)
                        </>
                      )}
                    </Typography>
                  </Box>
                )}
                {(status === "pending" || status === "draft") && reqTsMoment && (
                  <Box my={1}>
                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: ".80rem",
                        textTransform: "uppercase",
                        color: reqTsOverdue ? "error.main" : "#888",
                      }}
                    >
                      <AccessTimeOutlinedIcon sx={{ fontSize: "1.2rem", mr: "7px" }} />
                      PENDING ({reqTsMoment.fromNow()})
                    </Typography>
                  </Box>
                )}
                {status === "sent" && request.quote.resTs && (
                  <Box>
                    <Typography
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: ".80rem",
                        textTransform: "uppercase",
                        color: "#888",
                      }}
                    >
                      <ScheduleSendOutlinedIcon
                        style={{ fontSize: "1.2rem", marginRight: "7px" }}
                      />
                      {`QUOTED (${moment(request.quote.resTs._seconds * 1000).fromNow()})`}
                    </Typography>
                  </Box>
                )}
                {actionRow?.unavailableLabel && (
                  <Box my={1}>
                    <Typography variant="body1">{actionRow.unavailableLabel}</Typography>
                  </Box>
                )}
              </Box>
              {actionRow && (
                <Box sx={{ ...actionBoxSx, mt: 1, flexShrink: 0 }}>
                  {actionRow.showAutoQuote && request.quote?.autoQuote && (
                    <AutoAwesomeOutlinedIcon sx={{ color: "#999" }} />
                  )}
                  {request.src === "mkt" && (
                    <img
                      alt=""
                      src={bakeStreetSquareLogo}
                      style={{ width: "32px", height: "32px" }}
                    />
                  )}
                  {request.quote?.quotedBy && (
                    <UserAvatar
                      uid={request.quote.quotedBy}
                      style={{ width: "32px", height: "32px", marginRight: 0 }}
                    />
                  )}
                  <Button
                    disableElevation
                    variant={actionRow.variant}
                    color={actionRow.color}
                    component={RouterLink}
                    to={`/quote/${request.id}`}
                  >
                    {actionRow.buttonLabel}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
